/**
 * Changed layout of the dialog component.
 * added new sx styles and components.
 */

import { SessionErrorDialogLoginForm } from '@/components/content/SessionErrorDialog/parts/LoginForm';
import { SessionErrorDialogSx } from '@/components/content/SessionErrorDialog/styles/DialogContentError';
import { REMEMBERED_LOGON_ID } from '@/data/constants/cookie';
import {
	ACTIVITY_TOKEN_ERROR_KEY,
	COOKIE_EXPIRED_ERROR_KEY,
	INVALID_COOKIE_ERROR_KEY,
	PARTIAL_AUTHENTICATION_ERROR_KEY,
} from '@/data/constants/errors';
import { useSessionError } from '@/data/Content/SessionErrorDialog';
import { getCurrentUserFromStorage } from '@/data/forReactAndMobile/SaveCurrentUser';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { UserLogon } from '@/data/Content/Login';
import { logEvent } from '@/data/utils/logEventsUtils';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Dialog, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { getCookie } from 'shared/custom/utils/cookieHandler';

export const SessionErrorDialog: FC = () => {
	const { handleCancel, message, onSubmit, logonId, sessionError, setSessionError } =
		useSessionError();
	const [displayLogonId, setDisplayLogonId] = useState('');
	useEffect(() => {
		if (logonId) {
			setDisplayLogonId(logonId);
		} else {
			const cookieId = getCookie(REMEMBERED_LOGON_ID) || '';
			setDisplayLogonId(cookieId);
		}
	}, [logonId]);
	const { settings } = useSettings();
	const { user } = useUser();
	const isMobileApp = settings.isMobileApp;
	const hasLoggedMobileEventRef = useRef(false);
	const [openModal, setOpenModal] = useState(false);
	const currentUserStorage = getCurrentUserFromStorage(
		process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || ''
	);
	useEffect(() => {
		// The dialog should open if there's a sessionError and it's not a mobile app (where handling might differ)
		if (sessionError) {
			if (isMobileApp) {
				if (!hasLoggedMobileEventRef.current) {
					logEvent('openSessionExpiredDialog');
					hasLoggedMobileEventRef.current = true;
				}
				return;
			}
			const isGuest = user ? !user.isLoggedIn : !currentUserStorage?.userLoggedIn;
			const isGuestSessionExpired = Boolean(
				isGuest &&
					sessionError &&
					[
						INVALID_COOKIE_ERROR_KEY,
						ACTIVITY_TOKEN_ERROR_KEY,
						PARTIAL_AUTHENTICATION_ERROR_KEY,
						COOKIE_EXPIRED_ERROR_KEY,
					].includes(sessionError?.error.errorMessage)
			);
			if (isGuestSessionExpired) {
				handleCancel(undefined);
				setOpenModal(false); // Explicitly close for guest session errors after handling
			} else {
				setOpenModal(true);
			}
		} else {
			// No session error, or it's a mobile app, so ensure the modal is closed
			setOpenModal(false);
			hasLoggedMobileEventRef.current = false;
		}
	}, [sessionError, isMobileApp, currentUserStorage, user, handleCancel]);

	const innerCancel = () => {
		setSessionError(null);
		setOpenModal(false);
		handleCancel();
	};

	const innerSubmit = ({ email, logonId, logonPassword, rememberMe }: UserLogon) => {
		setSessionError(null);
		setOpenModal(false);
		onSubmit({ email, logonId, logonPassword, rememberMe });
	};

	return (
		<Dialog
			disableEscapeKeyDown
			sx={SessionErrorDialogSx.dialog}
			open={openModal}
			onClose={() => innerCancel()}
			id="session-error-dialog"
		>
			<DialogTitle sx={SessionErrorDialogSx.title}>
				<Typography variant="headingSegment" sx={SessionErrorDialogSx.titleText}>
					{message?.title}
				</Typography>
				<HighlightOffIcon sx={SessionErrorDialogSx.closeIcon} onClick={innerCancel} />
			</DialogTitle>
			<Divider />
			<DialogContent sx={SessionErrorDialogSx.content}>
				<SessionErrorDialogLoginForm
					handleCancel={innerCancel}
					onSubmit={innerSubmit}
					text={message?.text}
					logonId={displayLogonId}
				/>
			</DialogContent>
		</Dialog>
	);
};
